import { computed, onMounted, ref } from 'vue';

import { $mxsFetch } from '@/utils/fetch';

import { StorageSerializers, useEventListener, useStorage } from '@vueuse/core';

export function useSecurity() {
  const isSecurityAdmin = useStorage<boolean>('is_security_admin', false, localStorage, {
    mergeDefaults: true
  });

  const userPermissions = useStorage<Array<SecurityPermission>>(
    'user_permissions',
    [],
    localStorage,
    { mergeDefaults: true }
  );

  const canExport = computed(() =>
    userPermissions.value.find(({ permission }) => permission === 'export')
  );

  const user = useStorage<SecurityUser | undefined>('user', null, localStorage, {
    mergeDefaults: true,
    serializer: StorageSerializers.object
  });

  onMounted(() => {
    useEventListener(window, 'update-data', () => {
      isSecurityAdmin.value = localStorage.getItem('is_security_admin') === 'true';
      userPermissions.value = localStorage.getItem('user_permissions')
        ? JSON.parse(localStorage.getItem('user_permissions') as string)
        : [];
      user.value = localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user') as string)
        : null;
    });
  });

  const hasPermission = (pagePermission: { page: string; permission: string }): boolean => {
    return !!userPermissions.value.find(
      userPermission =>
        userPermission.page === pagePermission.page &&
        userPermission.permission === pagePermission.permission
    );
  };

  const organizations = ref<Array<Organization & { label: string; value: number }>>([]);

  const fetchOrganizations = async () => {
    const response = await $mxsFetch<SecurityAPIResponse<Array<Organization>>>(
      '/organizations?page=1&limit=9999'
    );
    organizations.value =
      response?.data?.map((organization: Organization) => {
        return {
          ...organization,
          label: organization.name,
          value: organization.id
        };
      }) || [];
  };

  const organizationUsers = ref<Array<SecurityUser & { label: string }>>([]);

  const organizationRoles = ref<Array<SecurityRole>>([]);

  const fetchOrganizationUsersAndRoles = async () => {
    const [roles, users] = await Promise.all([
      $mxsFetch<SecurityAPIResponse<Array<SecurityRole>>>(
        `/roles?page=1&limit=100000&organizationId=${user.value?.organizationId}`
      ),
      $mxsFetch<SecurityAPIResponse<Array<SecurityUser>>>(
        `/users?page=1&limit=100000&organizationId=${user.value?.organizationId}`
      )
    ]);

    organizationRoles.value = roles?.data || [];

    organizationUsers.value =
      users?.data?.map(organizationUser => ({
        ...organizationUser,
        label: `${organizationUser.name} (${organizationUser.email})`
      })) || [];
  };

  return {
    user,
    canExport,
    isSecurityAdmin,
    organizations,
    organizationUsers,
    organizationRoles,
    hasPermission,
    fetchOrganizations,
    fetchOrganizationUsersAndRoles
  };
}
